import * as z from 'zod';
import { PartialAppearancePoseSchema } from '../../state/characterStatePose.ts';
import { ConditionSchema } from '../conditions.ts';

export const LayerImageOverrideSchema = z.object({
	image: z.string(),
	normalMapImage: z.string().optional(),
	/**
	 * Pose to use for calculating UV coordinates of vertices.
	 *
	 * EXPERIMENTAL - subject to change, will likely be merged with `scaling` options soon.
	 */
	uvPose: PartialAppearancePoseSchema.optional(),
	condition: ConditionSchema,
});
export type LayerImageOverride = z.infer<typeof LayerImageOverrideSchema>;

export const LAYER_PRIORITIES = [
	'BACKGROUND',
	'BELOW_BACK_HAIR',
	'BACK_HAIR',
	'ABOVE_BACK_HAIR',

	'BELOW_SOLE_LEFT',
	'SOLE_LEFT',

	'BELOW_SOLE_RIGHT',
	'SOLE_RIGHT',

	'BELOW_BODY',

	'BELOW_LEG_LEFT',
	'LEG_LEFT',
	'ABOVE_LEG_LEFT',

	'BELOW_LEG_RIGHT',
	'LEG_RIGHT',
	'ABOVE_LEG_RIGHT',

	'BODY',
	'BELOW_BREASTS',
	'BREASTS',
	'ABOVE_BODY',

	'BELOW_ARM_LEFT',
	'ARM_LEFT',
	'ABOVE_ARM_LEFT',

	'BELOW_ARM_RIGHT',
	'ARM_RIGHT',
	'ABOVE_ARM_RIGHT',

	'FRONT_HAIR',
	'ABOVE_FRONT_HAIR',
	'OVERLAY',
] as const;

// Some priority layers should get mirrored when layer get mirrored
export const PRIORITY_ORDER_MIRROR: Partial<Record<LayerPriority, LayerPriority>> = {
	BELOW_SOLE_LEFT: 'BELOW_SOLE_RIGHT',
	BELOW_SOLE_RIGHT: 'BELOW_SOLE_LEFT',

	SOLE_LEFT: 'SOLE_RIGHT',
	SOLE_RIGHT: 'SOLE_LEFT',

	BELOW_LEG_LEFT: 'BELOW_LEG_RIGHT',
	BELOW_LEG_RIGHT: 'BELOW_LEG_LEFT',

	LEG_LEFT: 'LEG_RIGHT',
	LEG_RIGHT: 'LEG_LEFT',

	ABOVE_LEG_LEFT: 'ABOVE_LEG_RIGHT',
	ABOVE_LEG_RIGHT: 'ABOVE_LEG_LEFT',

	BELOW_ARM_LEFT: 'BELOW_ARM_RIGHT',
	BELOW_ARM_RIGHT: 'BELOW_ARM_LEFT',

	ARM_LEFT: 'ARM_RIGHT',
	ARM_RIGHT: 'ARM_LEFT',

	ABOVE_ARM_LEFT: 'ABOVE_ARM_RIGHT',
	ABOVE_ARM_RIGHT: 'ABOVE_ARM_LEFT',
};
if (!(Object.entries(PRIORITY_ORDER_MIRROR)).every(([original, mirror]) => PRIORITY_ORDER_MIRROR[mirror] === original)) {
	throw new Error('PRIORITY_ORDER_MIRROR not valid');
}

export const LayerPrioritySchema = z.enum(LAYER_PRIORITIES);
export type LayerPriority = z.infer<typeof LayerPrioritySchema>;

export enum LayerMirror {
	NONE,
	/** Only imageOverrides are mirrored, points are selected */
	SELECT,
}
export const LayerMirrorSchema = z.enum(LayerMirror);

export enum LayerSide {
	LEFT,
	RIGHT,
}

export const LayerImageSettingSchema = z.object({
	image: z.string(),
	normalMapImage: z.string().optional(),
	/**
	 * Pose to use for calculating UV coordinates of vertices.
	 *
	 * EXPERIMENTAL - subject to change, will likely be merged with `scaling` options soon.
	 */
	uvPose: PartialAppearancePoseSchema.optional(),
	overrides: z.array(LayerImageOverrideSchema),
}).strict();
export type LayerImageSetting = z.infer<typeof LayerImageSettingSchema>;

export const RoomDeviceLayerImageOverrideSchema = z.object({
	image: z.string(),
	normalMapImage: z.string().optional(),
	condition: ConditionSchema,
});
export type RoomDeviceLayerImageOverride = z.infer<typeof RoomDeviceLayerImageOverrideSchema>;

export const RoomDeviceLayerImageSettingSchema = z.object({
	image: z.string(),
	normalMapImage: z.string().optional(),
	overrides: z.array(RoomDeviceLayerImageOverrideSchema).optional(),
}).strict();
export type RoomDeviceLayerImageSetting = z.infer<typeof RoomDeviceLayerImageSettingSchema>;

export const LayerStateOverridesSchema = z.object({
	color: z.number().optional(),
	alpha: z.number().optional(),
});
export type LayerStateOverrides = z.infer<typeof LayerStateOverridesSchema>;

export function MirrorPriority(priority: LayerPriority): LayerPriority {
	const mirrorPriority = PRIORITY_ORDER_MIRROR[priority];
	return mirrorPriority != null ? mirrorPriority : priority;
}

export const LayerNormalDataSchema = z.object({
	specularStrength: z.number().nonnegative(),
	roughness: z.number().nonnegative(),
});
export type LayerNormalData = z.infer<typeof LayerNormalDataSchema>;
